import { useQueryClient } from "@tanstack/react-query";
import {
  getGetGrowUpContractingLinkQueryOptions,
  useGetAssetAllocationOverview,
  useGetGrowUpContractingLink,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import type { GrowUpLinkResult, NsureClientCaseStateCaseStatus, PortfolioAssetAllocationOverview } from "Infrastructure/ApiZfp/Schema";
import { LocalStorage, useLocalStorage } from "Infrastructure/Hooks/useLocalStorage";
import type React from "react";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "../_Shared/InvestmentCaseContext";

type GoalsFulfillmentContextType = {
  selectedFilters: GoalFulfillmentFilterType[];
  toggleFilter: (filterId: GoalFulfillmentFilterType) => void;
  setFilterType: (filterId: GoalFulfillmentFilterType) => void;
  isFilterSelected: (filterId: GoalFulfillmentFilterType) => boolean;
  registerEvaluationWarning: (id: string, hasWarning: boolean) => void;
  hasAnyEvaluationWarning: boolean;
  registerEvaluationPending: (id: string, isPending: boolean) => void;
  hasAnyEvaluationPending: boolean;
  isInvestmentCaseLocked: boolean;
  lockedCaseStatus: NsureClientCaseStateCaseStatus | null;
  growUpContractingLink: GrowUpLinkResult | undefined;
  fetchGrowUpContractingLink: () => Promise<GrowUpLinkResult | undefined>;
  assetAllocationOverview: PortfolioAssetAllocationOverview | undefined;
  isAssetAllocationOverviewSuccess: boolean;
};

export enum GoalFulfillmentFilterType {
  PortfolioDetail = "portfolio",
  FutureAmount = "futureAmount",
}

const GoalsFulfillmentContext = createContext<GoalsFulfillmentContextType | undefined>(undefined);

type GoalsFulfillmentProviderProps = {
  children: ReactNode;
  clientGuid: string | undefined;
};

export const GoalsFulfillmentProvider: React.FC<GoalsFulfillmentProviderProps> = ({ children, clientGuid }) => {
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked, lockedCaseStatus } = useContext(InvestmentCaseContext);
  const queryClient = useQueryClient();
  const { data: growUpContractingLink } = useGetGrowUpContractingLink(analysisId, versionId, clientGuid ?? "", {
    query: { enabled: !!clientGuid && isInvestmentCaseLocked },
  });

  const { data: assetAllocationOverview, isSuccess: isAssetAllocationOverviewSuccess } = useGetAssetAllocationOverview(
    analysisId,
    versionId,
  );

  const fetchGrowUpContractingLink = useCallback(async () => {
    if (!clientGuid) return undefined;
    return queryClient.fetchQuery(getGetGrowUpContractingLinkQueryOptions(analysisId, versionId, clientGuid));
  }, [queryClient, analysisId, versionId, clientGuid]);

  const [isFutureAmountEnabled, setIsFutureAmountEnabled] = useLocalStorage<boolean>(
    LocalStorage.GoalFulfillmentFutureAmountEnabled,
    false,
  );

  const [selectedFilters, setSelectedFilters] = useState<GoalFulfillmentFilterType[]>(() => {
    return isFutureAmountEnabled ? [GoalFulfillmentFilterType.FutureAmount] : [];
  });

  const [evaluationWarnings, setEvaluationWarnings] = useState<Record<string, boolean>>({});
  const [evaluationPendings, setEvaluationPendings] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const isFutureAmountSelected = selectedFilters.includes(GoalFulfillmentFilterType.FutureAmount);
    if (isFutureAmountSelected !== isFutureAmountEnabled) {
      setIsFutureAmountEnabled(isFutureAmountSelected);
    }
  }, [selectedFilters, isFutureAmountEnabled, setIsFutureAmountEnabled]);

  const toggleFilter = useCallback((filterId: GoalFulfillmentFilterType) => {
    setSelectedFilters((prev) => (prev.includes(filterId) ? prev.filter((id) => id !== filterId) : [...prev, filterId]));
  }, []);

  const setFilterType = useCallback((filterId: GoalFulfillmentFilterType) => {
    setSelectedFilters((prev) => (prev.includes(filterId) ? prev : [...prev, filterId]));
  }, []);

  const isFilterSelected = useCallback((filterId: GoalFulfillmentFilterType) => selectedFilters.includes(filterId), [selectedFilters]);

  const registerEvaluationWarning = useCallback((id: string, hasWarning: boolean) => {
    setEvaluationWarnings((prev) => ({ ...prev, [id]: hasWarning }));
  }, []);

  const registerEvaluationPending = useCallback((id: string, isPending: boolean) => {
    setEvaluationPendings((prev) => ({ ...prev, [id]: isPending }));
  }, []);

  const hasAnyEvaluationWarning = useMemo(() => Object.values(evaluationWarnings).some(Boolean), [evaluationWarnings]);
  const hasAnyEvaluationPending = useMemo(() => Object.values(evaluationPendings).some(Boolean), [evaluationPendings]);

  const contextValue = useMemo(
    () => ({
      selectedFilters,
      toggleFilter,
      setFilterType,
      isFilterSelected,
      registerEvaluationWarning,
      hasAnyEvaluationWarning,
      registerEvaluationPending,
      hasAnyEvaluationPending,
      isInvestmentCaseLocked,
      lockedCaseStatus,
      growUpContractingLink,
      fetchGrowUpContractingLink,
      assetAllocationOverview,
      isAssetAllocationOverviewSuccess,
    }),
    [
      selectedFilters,
      toggleFilter,
      setFilterType,
      isFilterSelected,
      registerEvaluationWarning,
      hasAnyEvaluationWarning,
      registerEvaluationPending,
      hasAnyEvaluationPending,
      isInvestmentCaseLocked,
      lockedCaseStatus,
      growUpContractingLink,
      fetchGrowUpContractingLink,
      assetAllocationOverview,
      isAssetAllocationOverviewSuccess,
    ],
  );

  return <GoalsFulfillmentContext.Provider value={contextValue}>{children}</GoalsFulfillmentContext.Provider>;
};

export const useGoalsFulfillmentContext = () => {
  const context = useContext(GoalsFulfillmentContext);
  if (!context) {
    throw new Error("useGoalsFulfillmentContext must be used within a GoalsFulfillmentProvider");
  }
  return context;
};
